
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { faChevronDown, faChevronUp, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ConditionalRendering } from '../../../members/utils';
import {
    StyledConfidentialTicketIds,
    StyledConfidentialTicketMeta,
    StyledConfidentialTicketToggle,
    StyledConfidentialTicketWarning
} from '../constants/ticketlist.styled';

interface IConfidentialTicketsInfoBannerProps {
    confidentialTicketList: Array<string | number>;
}

export const ConfidentialTicketsInfoBanner = ({ confidentialTicketList }: IConfidentialTicketsInfoBannerProps) => {
    const intl = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const idsRef = useRef<HTMLDivElement | null>(null);
    const effectiveHiddenCount = confidentialTicketList.length;
    const ticketList = confidentialTicketList.join(', ');
    const shouldShowToggle = isExpanded || isOverflowing;

    useEffect(() => {
        setIsExpanded(false);
    }, [ticketList]);

    useLayoutEffect(() => {
        const node = idsRef.current;
        if (!node) {
            return;
        }

        const measureOverflow = () => {
            if (isExpanded) {
                return;
            }
            setIsOverflowing(node.scrollWidth > node.clientWidth);
        };

        measureOverflow();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(measureOverflow);
        observer.observe(node);
        return () => observer.disconnect();
    }, [ticketList, isExpanded]);

    return (
        <StyledConfidentialTicketWarning
            className="usa-alert gi-alert gi-alert__left-accent gi-alert__warning"
            data-testid="confidential-ticket-warning"
        >
            <div className="gi-alert__body">
                <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="confidential-ticket-access-warning__icon"
                    aria-hidden="true"
                />
                <p className="gi-alert__text">
                    {intl.formatMessage({ id: 'ticket.sah.msg.hiddenSearchResults' })}
                </p>
                <StyledConfidentialTicketMeta data-testid="confidential-ticket-meta">
                    {intl.formatMessage({ id: 'ticket.sah.header.confidentialTickets' })} ({effectiveHiddenCount})
                </StyledConfidentialTicketMeta>
                <StyledConfidentialTicketIds
                    ref={idsRef}
                    id={'confidential-ticket-ids'}
                    data-testid="confidential-ticket-ids"
                    $isExpanded={isExpanded}
                >
                    {ticketList}
                </StyledConfidentialTicketIds>

                <ConditionalRendering isTrue={shouldShowToggle}>
                    <StyledConfidentialTicketToggle
                        type="button"
                        onClick={() => setIsExpanded((prev) => !prev)}
                        aria-expanded={isExpanded}
                        aria-controls="confidential-ticket-ids"
                        data-testid="confidential-ticket-toggle"
                    >
                        {isExpanded
                            ? intl.formatMessage({ id: 'ticket.sah.btn.viewLess' })
                            : intl.formatMessage({ id: 'ticket.sah.btn.viewMore' })}
                        <FontAwesomeIcon
                            icon={isExpanded ? faChevronUp : faChevronDown}
                            aria-hidden="true"
                        />
                    </StyledConfidentialTicketToggle>
                </ConditionalRendering>
            </div>
        </StyledConfidentialTicketWarning>
    );
};
